/**
 * Where a building may stand — the one predicate the ghost, the tile frame and the
 * click all read.
 *
 * The rule the owner asked for is "under a building there can't be bushes or other
 * elements", and it was genuinely absent: eligibility only tested the collision
 * grid, which by design marks a NARROW set (tree / boulder / ore_rock / hillock /
 * mountain). Bushes were deliberately made non-solid so the hero can walk through
 * them, and rocks, mushrooms and wildflowers were never in the grid at all — so a
 * house dropped straight on top of them, art and all. Walkability and buildability
 * are simply different questions, and this file is where the second one is answered.
 *
 * It also tests the FOOTPRINT rather than the anchor tile. A house occupies 3x2;
 * only the "would this trap the hero" check ever looked past the single tile, so a
 * cottage could legally cover five tiles of trees, beds or another building.
 *
 * SERVER SPLIT, stated plainly: the world is generated on the client from a seed,
 * so the server has no terrain, no decor and no hero position, and cannot validate
 * any of it — pretending otherwise would be theatre. It enforces exactly what it
 * owns (the tile is on the map, no building/plot is already there, the caps, the
 * population gate, the material cost, the churn budget); everything map-shaped is
 * enforced here, honestly and in one place, so at least it cannot drift between the
 * preview and the click.
 *
 * The predicate takes a plain {@link PlacementQuery} rather than the whole FarmCtx
 * so it can be reasoned about — and tested — without a Pixi application.
 */
import { isDecorBudgetKind, isGroundKind } from '../../map/decor-kinds';
import { isFenceKind } from '../../map/fence-connect';
import { TILE } from '../../map/world';
import { FOOT_OFFSET } from '../../map/player';
import { solidBuildingTiles, tileBlocked } from './scene';
import { BUILDINGS, TALL_KINDS, GATHER_KINDS, bkey, buildingAt, plotAt, isCleared, isGathered, isMined, type FarmCtx } from './context';

/** Bridge decks are the one thing that belongs ON water — and only there. */
export const isBridgeKind = (kind: string): boolean => kind.startsWith('bridge_');

/**
 * The TENT is placeable but is NOT a building: the player owns exactly one from
 * the moment he starts, it costs nothing, it occupies one tile, and it lives on
 * the farm row (tent_tx/tent_ty) rather than in the buildings table — see the
 * server's game/field/tent.ts for why. It still has to go through this file,
 * because "where may this stand" is one question and answering it twice is how the
 * ghost and the click end up disagreeing.
 */
export const TENT_KIND = 'tent';

/**
 * How the pitched tent is DRAWN, shared with the placement ghost.
 *
 * The tent is the one placeable whose art is not 16px pack art: it is a 48x96
 * building cell, so the usual `buildingScale` (TILE/16, plus the +20% weight
 * bump) drew it more than twice its real size. The renderer had its own private
 * constant for that and the ghost did not, so the preview was a giant tent and
 * what you got was a small one — the reported "built buildings are smaller than
 * their ghost". One constant, one anchor, both used by both.
 *
 * `TENT_ANCHOR_Y` goes with it: the tent is pinned slightly above its tile's
 * bottom edge (0.9) so the guy-ropes sit on the tile rather than below it, and a
 * ghost anchored at 1.0 would preview it one-tenth of a tile low.
 */
export const TENT_SCALE = 1.725;
export const TENT_ANCHOR_Y = 0.9;

/**
 * Safe radius in tiles — a MIRROR of the server's TENT_SAFE_RADIUS_TILES, used
 * only until `field/tent` has answered. The server's number is the real one (it
 * ships `safeRadius` in every tent payload); this constant exists so the first
 * frame after a reload doesn't draw a zone of size zero.
 */
export const TENT_SAFE_RADIUS_TILES = 6;

/** Is a hero tile inside the tent's safe zone? Circular, matching the server. */
export function isInsideSafeZone(
  tent: { tx: number; ty: number } | null,
  tx: number,
  ty: number,
  radiusTiles: number = TENT_SAFE_RADIUS_TILES,
): boolean {
  if (!tent) return false;
  const dx = tx - tent.tx, dy = ty - tent.ty;
  return dx * dx + dy * dy <= radiusTiles * radiusTiles;
}

/**
 * Every tile a placement consumes.
 *
 * `solidBuildingTiles` is the collision footprint, which is empty for the kinds
 * that block nothing (roads, torches) — so the anchor is always included: even a
 * torch may not share a tile with a tree.
 */
export function footprintTiles(kind: string, tx: number, ty: number): [number, number][] {
  const solid = solidBuildingTiles(kind, tx, ty);
  if (!solid.some(([x, y]) => x === tx && y === ty)) solid.push([tx, ty]);
  return solid;
}

/**
 * Clearance around a building — OWNER (2026-08-14): "from front to building
 * enough 1, from side it should be 2 blocks."
 *
 * That matches how the art is drawn: a building sprite is anchored at the bottom
 * of its tile and painted upward and outward, so it covers two tiles either side
 * and two above its footprint, but nothing BELOW the anchor — the front step is
 * the sprite's bottom edge. A bed inside that box is drawn UNDER the building and
 * is lost to the player.
 *
 * Lived in input.ts, where only the hoe could see it. It is here now because BOTH
 * directions of the rule need it: the hoe refuses to till inside the box, and a
 * placement DESTROYS the beds inside it (owner, 2026-08-17).
 */
export const BUILDING_CLEAR = { side: 2, back: 2, front: 1 } as const;

/**
 * Every tile a placement RESERVES: the footprint plus the clearance ring, as the
 * bounding box of the footprint grown by {@link BUILDING_CLEAR}.
 *
 * ONE set, read both ways. tillBlocker refuses a bed inside it and build()
 * deletes the beds inside it — if the two sets differed, the player could pay
 * energy for a bed the very next placement silently destroyed, or keep a bed that
 * ends up drawn under a wall. Mirrored server-side in
 * backend/src/game/field/building.footprint.ts, which owns the delete.
 */
export function reservedBox(kind: string, tx: number, ty: number):
  { x0: number; x1: number; y0: number; y1: number } {
  let x0 = Infinity, x1 = -Infinity, y0 = Infinity, y1 = -Infinity;
  for (const [x, y] of footprintTiles(kind, tx, ty)) {
    x0 = Math.min(x0, x); x1 = Math.max(x1, x);
    y0 = Math.min(y0, y); y1 = Math.max(y1, y);
  }
  return {
    x0: x0 - BUILDING_CLEAR.side, x1: x1 + BUILDING_CLEAR.side,
    y0: y0 - BUILDING_CLEAR.back, y1: y1 + BUILDING_CLEAR.front,
  };
}

export function reservedTiles(kind: string, tx: number, ty: number): [number, number][] {
  const { x0, x1, y0, y1 } = reservedBox(kind, tx, ty);
  const out: [number, number][] = [];
  // -y is north — "behind" the building, the way the art grows; +y is its front.
  for (let y = y0; y <= y1; y++) for (let x = x0; x <= x1; x++) out.push([x, y]);
  return out;
}

/**
 * Does placing this kind destroy the beds it reserves?
 *
 * Roads, paths and bridge decks are drawn FLAT, beneath the soil — they cover
 * nothing, so they clear nothing. Deleting a row of crops to pave a path would be
 * precisely the "it wrecked my farm" report this whole warning exists to avoid.
 * They still may not be laid on a bed's own tile (see placementBlocker).
 * Mirrors `destroysPlots` in the server's building.footprint.ts.
 */
export const destroysPlots = (kind: string): boolean => !isGroundKind(kind);

/**
 * The beds a placement would destroy — what the confirmation counts, and what the
 * server is about to delete.
 */
export function plotsDestroyedBy<T extends { tx: number; ty: number }>(
  plots: readonly T[], kind: string, tx: number, ty: number,
): T[] {
  if (!destroysPlots(kind)) return [];
  const reserved = new Set(reservedTiles(kind, tx, ty).map(([x, y]) => `${x},${y}`));
  return plots.filter((p) => reserved.has(`${p.tx},${p.ty}`));
}

/**
 * Is this tile inside the space an ALREADY PLACED building reserves?
 *
 * Box test rather than reservedTiles(): this runs from tillBlocker, which the tile
 * bracket calls on every pointer move against every building on the farm (fences
 * and road tiles are buildings too, and are laid by the hundred) — materialising a
 * ~20-tile array per building per frame is a lot of garbage for one boolean.
 */
export function insideReservedSpace(
  buildings: readonly { kind: string; tx: number; ty: number }[], tx: number, ty: number,
): boolean {
  return buildings.some((b) => {
    const r = reservedBox(b.kind, b.tx, b.ty);
    return tx >= r.x0 && tx <= r.x1 && ty >= r.y0 && ty <= r.y1;
  });
}

/** Everything the placement rules need to know about the world, as data. */
export interface PlacementQuery {
  cols: number;
  rows: number;
  /** Terrain kind of a tile ('grass' / 'water' / …). */
  terrain(tx: number, ty: number): string;
  /** Collision grid: tree, cliff, water, an existing structure. */
  blocked(tx: number, ty: number): boolean;
  /** A prop ANCHORED on this tile — bush, rock, mushroom, tree, boulder. */
  decor(tx: number, ty: number): boolean;
  /** A soil bed here. */
  plot(tx: number, ty: number): boolean;
  /** A placed structure here. */
  building(tx: number, ty: number): boolean;
  /** The tile the hero stands on, or null when there is no hero. */
  hero: [number, number] | null;
  /** Room left in the budget this kind is charged against. */
  hasRoom: boolean;
  /** Villagers on the farm, or null when it has not been asked yet. */
  population: number | null;
}

/**
 * Why this placement is refused, as a sentence for the player — or null when it is
 * allowed. A reason string rather than a boolean because the ghost SHOWS it: a red
 * outline that doesn't say what is wrong is the same dead click by another name.
 */
export function placementBlocker(q: PlacementQuery, kind: string, tx: number, ty: number): string | null {
  // The tent is not in BUILDINGS and never will be: no cost, no cap slot, no
  // population gate. It only needs the TERRAIN half of the rules below, which is
  // exactly the half the server cannot check.
  const tent = kind === TENT_KIND;
  const def = tent ? { name: 'Tent' } as (typeof BUILDINGS)[string] : BUILDINGS[kind];
  if (!def) return 'Unknown building';
  if (!tent && !q.hasRoom) return 'No room left — demolish something first';
  // Population gate (server: BUILD_UNLOCK_POP). Unknown population is treated as
  // allowed: refusing on missing data would lock the panel for anyone whose village
  // fetch failed, and the server still has the last word.
  if (def.needPop && q.population !== null && q.population < def.needPop) {
    return `Needs ${def.needPop} villagers — summon more in the Village`;
  }

  const bridge = isBridgeKind(kind);
  const ground = isGroundKind(kind);
  for (const [x, y] of footprintTiles(kind, tx, ty)) {
    if (x < 0 || y < 0 || x >= q.cols || y >= q.rows) return 'Outside the map';
    // Structures are named BEFORE the collision grid, which also marks placed
    // buildings: "something is in the way" for a tile with your own barn on it is
    // technically true and completely useless.
    //
    // A BED no longer refuses a real building. Owner (2026-08-17): a building
    // raised over plots destroys them — see plotsDestroyedBy and the server's
    // build(). The refusal survives for the two kinds that clear NOTHING, where
    // dropping it would leave a live soil row buried forever: ground paving (drawn
    // flat, under the soil) and the TENT, which never reaches build() at all — it
    // goes down the field/tent endpoint, which deletes no plots.
    if ((ground || tent) && q.plot(x, y)) return 'A soil bed is already there';
    if (q.building(x, y)) return 'Something is already built there';
    if (bridge) {
      if (q.terrain(x, y) !== 'water') return 'A bridge deck goes on water';
    } else {
      if (q.terrain(x, y) !== 'grass') return 'Only on clear ground';
      if (q.blocked(x, y)) return 'Something is in the way';
      // Props are the owner's rule. Ground paving is exempt: a road tile is drawn
      // flat UNDER everything, so paving past a flower bothers nobody — and trees
      // and boulders already stopped it via the collision grid above.
      if (!ground && q.decor(x, y)) return 'Clear the bushes, rocks and props first';
    }
  }
  // Movement only tests the tile it steps INTO, so a solid building placed on the
  // hero's tile freezes him where he stands — WASD dead and no route out of a
  // blocked start tile. Only the SOLID footprint traps him; you may stand on a road.
  if (q.hero && solidBuildingTiles(kind, tx, ty).some(([x, y]) => x === q.hero![0] && y === q.hero![1])) {
    return 'You are standing there — step aside first';
  }
  return null;
}

/**
 * Adapt the live farm view to {@link PlacementQuery}.
 *
 * `ignore` exempts the tiles of the building currently being MOVED: it is still on
 * the map (and in the collision grid) while its ghost is in flight, so without this
 * nothing could ever be nudged one tile sideways — it would collide with itself.
 */
export function placementQuery(c: FarmCtx, ignore?: { tx: number; ty: number; kind: string } | null): PlacementQuery {
  const world = c.world;
  const skip = new Set(ignore ? footprintTiles(ignore.kind, ignore.tx, ignore.ty).map(([x, y]) => `${x},${y}`) : []);
  const exempt = (tx: number, ty: number) => skip.has(`${tx},${ty}`);
  return {
    cols: world?.cols ?? 0,
    rows: world?.rows ?? 0,
    terrain: (tx, ty) => (world ? world.terrain[ty * world.cols + tx] : 'void'),
    blocked: (tx, ty) => !exempt(tx, ty) && tileBlocked(c, tx, ty),
    decor: (tx, ty) => {
      const list = c.tallBuckets.get(bkey(tx, ty));
      // Only props that are STILL THERE block. A harvested node stays in the
      // bucket until it regrows, so without this the refusal 'clear the props
      // first' had no action that could satisfy it: you grubbed the stump (which
      // buildCollision already frees for walking and tilling), gathered the bush,
      // mined the rock — and the ghost stayed red.
      return !!list?.some((d) => d.tx === tx && d.ty === ty
        && (TALL_KINDS.has(d.kind) || GATHER_KINDS.has(d.kind))
        && !isCleared(c, d) && !isGathered(c, d) && !isMined(c, d));
    },
    plot: (tx, ty) => !!plotAt(c, tx, ty),
    building: (tx, ty) => !exempt(tx, ty) && !!buildingAt(c, tx, ty),
    hero: c.player ? [Math.floor(c.player.x / TILE), Math.floor((c.player.y - FOOT_OFFSET) / TILE)] : null,
    // A move creates nothing, so it spends no cap slot — only a fresh placement is
    // measured against the budget.
    hasRoom: ignore ? true : hasRoomFor(c, c.buildMode ?? ''),
    population: c.population,
  };
}

/**
 * Room left in the budget the SELECTED kind is charged against. The server keeps
 * three separate buckets (structures / fences / decoration); testing every kind
 * against the structure cap meant 20 road tiles locked the player out of every
 * house, which is exactly what split budgets exist to prevent.
 *
 * An empty kind means "not creating anything" (a move consumes no cap slot), which
 * always has room.
 */
export function hasRoomFor(c: FarmCtx, kind: string): boolean {
  if (!kind) return true;
  if (kind === TENT_KIND) return true;   // you already own it — nothing is being created
  const f = c.farm;
  if (!f) return false;
  if (isFenceKind(kind)) return f.maxFences === undefined ? true : (f.fenceCount ?? 0) < f.maxFences;
  // Stations and lights bill here too, not to the building cap (isDecorBudgetKind).
  if (isDecorBudgetKind(kind)) return f.maxDecor === undefined ? true : (f.decorCount ?? 0) < f.maxDecor;
  return f.buildingCount < f.maxBuildings;
}

/** Can the armed (or moving) building be placed at this tile? */
export const canBuildHere = (c: FarmCtx, tx: number, ty: number): boolean =>
  buildBlocker(c, tx, ty) === null;

/** The reason the armed (or moving) building cannot go here, or null. */
export function buildBlocker(c: FarmCtx, tx: number, ty: number): string | null {
  const kind = c.buildMode;
  if (!kind || !c.farm) return 'Nothing selected';
  return placementBlocker(placementQuery(c, c.moving), kind, tx, ty);
}
